import { model as i18n } from '!/i18n'
import { hooks } from '!/router'
import { register } from '!/repository'
import { aye } from '@/helpers'
import { profilesModel } from '~/entities/profiles'
import { model as session } from '~/entities/session'
import { combine, createEvent, createStore, restore, sample } from 'effector'
import { $subscriberPin, unlockId } from '../content'

export interface IPinLockPopupProps {
  condition: boolean
  disabled: boolean
  errorMessage: string
  id: number | null
  invalid: boolean
  locked: boolean
  name: string
  onChange: (value: string) => void
  onComplete: (value: string) => void
  valueAfterInvalid: string // it is necessary not to lose the character input after the arrival of an external invalid prop. which leads to the component being unmounted
}

export const emptyPinLockState: IPinLockPopupProps = {
  condition: false,
  disabled: false,
  errorMessage: '',
  id: null,
  invalid: false,
  locked: false,
  name: '',
  onChange: () => null,
  onComplete: () => null,
  valueAfterInvalid: '',
}

export const openPinLockPopup = createEvent<IPinLockPopupProps>()
export const closePinLockPopup = createEvent()
export const autoSubmitPinCode = createEvent<{ value: string }>()
export const changePinCode = createEvent<{ value: string }>()
export const resetValidPinCode = createEvent()

export const $isValidPinCode = createStore<boolean>(false) //
  .reset([openPinLockPopup, resetValidPinCode, closePinLockPopup])

export const $pinLockPopup = createStore<IPinLockPopupProps>(emptyPinLockState)
  .on(openPinLockPopup, (_, payload) => ({ ...payload, condition: true }))
  .reset(closePinLockPopup)

export const $pinLockPopupDescription = i18n.translated`locked_popup_hint`
const $pinLockPopupErrorMessage = i18n.translated`pin_locked_error`

sample({
  clock: $isValidPinCode,
  source: restore(openPinLockPopup, null).reset(unlockId),
  filter: (targetChannel, valid) => aye(valid) && targetChannel != null,
  fn: (targetChannel) => targetChannel?.id,
  target: [unlockId, closePinLockPopup],
})

sample({
  clock: autoSubmitPinCode,
  source: {
    pin: $subscriberPin,
    popup: $pinLockPopup,
    errorMessageLocale: $pinLockPopupErrorMessage,
  },
  fn: ({ pin, popup, errorMessageLocale }, { value }) => {
    const invalid = pin !== value
    const errorMessage = invalid ? errorMessageLocale : ''
    return {
      ...popup,
      invalid,
      valueAfterInvalid: '',
      errorMessage,
    }
  },
  target: $pinLockPopup,
})

sample({
  clock: autoSubmitPinCode,
  source: { pin: $subscriberPin },
  fn: ({ pin }, { value }) => pin === value,
  target: $isValidPinCode,
})

// remove error state when new valid value provided
sample({
  clock: changePinCode,
  source: $pinLockPopup,
  filter: ({ invalid }, { value }) => invalid && Boolean(value.length),
  fn: (state, { value }) => {
    return {
      ...state,
      invalid: false,
      valueAfterInvalid: value,
      errorMessage: '',
    }
  },
  target: $pinLockPopup,
})

//
// Reset and hide pin lock popup on any route leave
//

const navigationLeftHook = createEvent()

// register the navigation hook
hooks.register(hooks.Left, navigationLeftHook)

sample({
  clock: navigationLeftHook,
  fn: () => undefined,
  target: closePinLockPopup,
})

const unlockContent =
  createEvent<Pick<IPinLockPopupProps, 'id' | 'name' | 'locked'>>()

sample({
  clock: unlockContent,
  fn: ({ id, name, locked }) => ({
    ...emptyPinLockState,
    id,
    name,
    locked,
  }),
  target: openPinLockPopup,
})

//
// Reset and hide pin lock popup on any session change
//

sample({
  clock: [session.$accountNumber, profilesModel.$activeProfileId],
  fn: () => undefined,
  target: closePinLockPopup,
})

/**
 * Disabling automatic scrolling when the pinlock popup is open
 */
const $isBannerAutoScrollEnabled = combine(
  $pinLockPopup,
  (pinLockPopup) => (autoScrollEnabled: boolean) =>
    autoScrollEnabled && !pinLockPopup.condition
)

//
//
//

register($pinLockPopup, '$features/pin-lock/popup')
// register(openPinLockPopup, 'features/pin-lock/openPopup')
register(closePinLockPopup, 'features/pin-lock/closePopup')
register(changePinCode, 'features/pin-lock/changePinCode')
register(autoSubmitPinCode, 'features/pin-lock/autoSubmitPinCode')
register(unlockContent, 'features/pin-lock/unlockContent')
register(
  $isBannerAutoScrollEnabled,
  '|features/pin-lock/isBannerAutoScrollEnabled'
)
